import React, { forwardRef } from "react";
import type { BaseProps } from "@purpur/common-types";
import c from "classnames/bind";

import { PaginationPageTrigger } from "./pagination-page-trigger";
import styles from "./pagination-pages.module.scss";
import { PaginationTruncationSeparator } from "./pagination-truncation-separator";
import { type InternalLinkProps, type InternalNoLinkProps, type Link } from "./types";

const cx = c.bind(styles);

export type PaginationPagesProps = Omit<BaseProps<"ol">, "children"> & {
  linkElement: Link;
  currentPage: number;
  onPageChange: (page: number) => void;
  pages: number[];
  stepNumberPrefix: string;
  truncationSeparatorAriaLabel?: string;
} & (InternalNoLinkProps | InternalLinkProps);

const rootClassName = "purpur-pagination-pages";

export const PaginationPages = forwardRef<HTMLOListElement, PaginationPagesProps>(
  (
    {
      ["data-testid"]: dataTestId = "purpur-pagination-pages",
      linkElement: LinkElement,
      asLink,
      className,
      currentPage,
      hrefGetter,
      onPageChange,
      pages,
      stepNumberPrefix,
      truncationSeparatorAriaLabel,
      ...props
    },
    ref
  ) => {
    const classes = cx(className, rootClassName);

    return (
      <ol className={classes} data-testid={dataTestId} ref={ref} {...props}>
        {pages.flatMap((pageNumber: number, idx: number, pages: number[]) => {
          const TriggerComponent = (
            <PaginationPageTrigger
              {...(asLink
                ? { asLink: true, hrefGetter }
                : {
                    asLink: undefined as never,
                    hrefGetter: undefined as never,
                  })}
              linkElement={LinkElement}
              data-testid={`${dataTestId}-page-${pageNumber}`}
              key={pageNumber}
              onPageChange={onPageChange}
              selected={pageNumber === currentPage}
              stepNumberPrefix={stepNumberPrefix}
              pageNumber={pageNumber}
            />
          );
          return idx === 0 || pages[idx - 1] + 1 === pageNumber
            ? [TriggerComponent]
            : [
                <PaginationTruncationSeparator
                  data-testid={`${dataTestId}-truncation-separator-${pageNumber}`}
                  key={`${pageNumber}-separator`}
                  label={truncationSeparatorAriaLabel}
                />,
                TriggerComponent,
              ];
        })}
      </ol>
    );
  }
);

PaginationPages.displayName = "PaginationPages";
